import { nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue'

// Only popup geometry and scrolling: no catalogue, routing or search rules.
export function useListboxViewport(
  input: Ref<HTMLInputElement | null>,
  panel: Ref<HTMLElement | null>,
  query: Ref<string>,
  activeIndex: Ref<number>
) {
  const placement = ref<'above' | 'below'>('below')
  const maxHeight = ref(320)
  let disposed = false

  async function update(ensureInput = false) {
    await nextTick()
    if (disposed || !query.value || !input.value || !panel.value) return
    const field = input.value
    let rect = field.getBoundingClientRect()
    if (!field.isConnected || !rect.height) return
    const viewport = window.visualViewport
    const top = (viewport?.offsetTop ?? 0) + 12
    const bottom = (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight) - 12
    if (ensureInput && (rect.top < top || rect.bottom > bottom)) {
      field.scrollIntoView({ block: 'center', behavior: 'auto' })
      rect = field.getBoundingClientRect()
    }
    const below = Math.max(0, bottom - rect.bottom - 8)
    const above = Math.max(0, rect.top - top - 8)
    placement.value = below < 200 && above > below ? 'above' : 'below'
    maxHeight.value = Math.floor(Math.min(320, placement.value === 'above' ? above : below))
    await nextTick()
    if (disposed || !query.value || !panel.value) return
    const list = panel.value
    const selected = list.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!selected || !list.clientHeight) return
    const listTop = list.getBoundingClientRect().top + list.clientTop
    const selectedRect = selected.getBoundingClientRect()
    if (selectedRect.top < listTop) list.scrollTop -= listTop - selectedRect.top
    else if (selectedRect.bottom > listTop + list.clientHeight) {
      list.scrollTop += selectedRect.bottom - listTop - list.clientHeight
    }
  }
  const onScroll = () => {
    void update()
  }
  const onResize = () => {
    void update(true)
  }
  watch(
    [query, activeIndex],
    () => {
      void update(true)
    },
    { flush: 'post' }
  )
  onMounted(() => {
    window.addEventListener('resize', onResize, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })
    window.visualViewport?.addEventListener('resize', onResize, { passive: true })
    window.visualViewport?.addEventListener('scroll', onScroll, { passive: true })
  })
  onBeforeUnmount(() => {
    disposed = true
    window.removeEventListener('resize', onResize)
    window.removeEventListener('scroll', onScroll)
    window.visualViewport?.removeEventListener('resize', onResize)
    window.visualViewport?.removeEventListener('scroll', onScroll)
  })
  return { placement, maxHeight }
}
